/**
 * Admin Panel Component
 * Allows admin to approve/deny user account requests
 */

import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './Auth.css';

const AdminPanel = ({ onLogout, onClose }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [existingUsers, setExistingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [requestsRes, usersRes] = await Promise.all([
        fetch('/api/auth/pending-requests'),
        fetch('/api/auth/users')
      ]);

      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        setPendingRequests(requestsData.requests || []);
      }

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setExistingUsers(usersData.users || []);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId, email, name) => {
    if (!window.confirm(`Approve account request for ${name} (${email})?`)) {
      return;
    }

    setProcessingId(requestId);
    setError('');
    setMessage('');

    try {
      const response = await fetch('/api/auth/approve-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ requestId, email, name })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to approve request');
      }

      setMessage(`Account approved for ${name}. Invitation email sent.`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to approve request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeny = async (requestId, email, name) => {
    if (!window.confirm(`Deny account request for ${name} (${email})?`)) {
      return;
    }

    setProcessingId(requestId);
    setError('');
    setMessage('');

    try {
      const response = await fetch('/api/auth/deny-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ requestId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deny request');
      }

      setMessage(`Account request denied for ${name}`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to deny request');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div>
          <h1>👑 Admin Panel</h1>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
            Manage user account requests and permissions
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {onClose && (
            <button
              className="btn-approve"
              onClick={onClose}
              style={{ padding: '10px 20px' }}
            >
              ← Back to App
            </button>
          )}
          <button className="btn-logout" onClick={onLogout}>
            Sign Out
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      <div className="pending-requests">
        <h2>📋 Pending Account Requests ({pendingRequests.length})</h2>
        
        {loading ? (
          <div className="empty-state">
            <p>Loading requests...</p>
          </div>
        ) : pendingRequests.length === 0 ? (
          <div className="empty-state">
            <p style={{ fontSize: '48px', margin: 0 }}>✓</p>
            <p>No pending requests</p>
          </div>
        ) : (
          <div className="request-list">
            {pendingRequests.map((request) => (
              <div key={request.id} className="request-card">
                <div className="request-info">
                  <h3>{request.name}</h3>
                  <p><strong>Email:</strong> {request.email}</p>
                  <p><strong>Requested:</strong> {formatDate(request.requestedAt)}</p>
                  {request.organization && (
                    <p><strong>Organization:</strong> {request.organization}</p>
                  )}
                </div>
                <div className="request-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(request.id, request.email, request.name)}
                    disabled={processingId === request.id}
                  >
                    {processingId === request.id ? 'Processing...' : '✓ Approve'}
                  </button>
                  <button
                    className="btn-deny"
                    onClick={() => handleDeny(request.id, request.email, request.name)}
                    disabled={processingId === request.id}
                  >
                    {processingId === request.id ? 'Processing...' : '✗ Deny'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="existing-users">
        <h2>👥 Existing Users ({existingUsers.length})</h2>
        
        {loading ? (
          <div className="empty-state">
            <p>Loading users...</p>
          </div>
        ) : existingUsers.length === 0 ? (
          <div className="empty-state">
            <p>No users found</p>
          </div>
        ) : (
          <div className="user-list">
            {existingUsers.map((user) => (
              <div key={user.username} className="user-card">
                <div className="user-info">
                  <h4>{user.name || user.username}</h4>
                  <p>{user.email}</p>
                  {user.createdAt && (
                    <p style={{ fontSize: '12px', marginTop: '5px' }}>
                      Created: {formatDate(user.createdAt)}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {user.isAdmin && (
                    <span className="user-status status-admin">👑 Admin</span>
                  )}
                  <span className={`user-status ${user.enabled ? 'status-enabled' : 'status-disabled'}`}>
                    {user.enabled ? '✓ Enabled' : '✗ Disabled'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
